/**
 * Hook to access and control the Babylon scene
 *
 * IMPORTANT: Scene objects are NOT stored in React state because:
 * 1. Scene objects are massive with circular references
 * 2. Havok physics bodies contain BigInt handles
 * 3. React 19 dev mode tries to serialize state, causing "cannot serialize BigInt" errors
 *
 * Instead, we store a version counter and access scenes via manager.getScene()
 */

import type { Scene } from "@babylonjs/core";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useBabylonContext } from "../BabylonContext";
import type { UseSceneReturn } from "../types";
import { useBabylonManager } from "./useBabylonManager";

/**
 * Hook to access and control the Babylon scene
 * @param sceneId Optional scene ID for multi-scene scenarios
 * @returns Scene instance and control functions
 */
export function useScene(overrideSceneId?: string): UseSceneReturn {
  const { manager, initialize } = useBabylonManager();
  const context = useBabylonContext();

  // Use override ID if provided, otherwise use context sceneId for multi-instance scenarios
  const sceneId = overrideSceneId || context.sceneId;

  // Version counter triggers re-renders when scene changes, without storing Scene in state
  // This prevents React 19 dev mode from trying to serialize BigInt values in physics bodies
  const [sceneVersion, setSceneVersion] = useState(0);

  // Derive scene from manager on each render (cheap operation)
  // This keeps the massive Scene object out of React state
  const scene = useMemo<Scene | null>(() => {
    // sceneVersion dependency ensures we re-compute when scene changes
    if (!manager || sceneVersion < 0) return null;
    return manager.getScene(sceneId);
  }, [manager, sceneId, sceneVersion]);

  const isReady = scene !== null;

  // Subscribe to scene events and bump version to trigger re-renders
  useEffect(() => {
    if (manager) {
      // Initial sync
      const currentScene = manager.getScene(sceneId);
      if (currentScene) {
        setSceneVersion((v) => v + 1);
      }

      // Subscribe to scene events
      const handleSceneCreated = (data: { scene: Scene; sceneId?: string }) => {
        // Only update if it matches our scene ID (or if we have no specific ID)
        if (!sceneId || data.sceneId === sceneId) {
          setSceneVersion((v) => v + 1);
        }
      };

      const handleSceneDisposed = (data?: { sceneId?: string }) => {
        // Only update if it matches our scene ID (or if we have no specific ID)
        if (!sceneId || data?.sceneId === sceneId) {
          setSceneVersion((v) => v + 1);
        }
      };

      const unsubscribeCreated = manager.onEvent(
        "sceneCreated",
        handleSceneCreated,
      );
      const unsubscribeDisposed = manager.onEvent(
        "sceneDisposed",
        handleSceneDisposed,
      );

      return () => {
        unsubscribeCreated();
        unsubscribeDisposed();
      };
    }
  }, [manager, sceneId]);

  // Create scene function
  const createScene = useCallback(async (): Promise<Scene> => {
    if (!manager) {
      throw new Error("BabylonManager is not initialized");
    }

    if (!manager.isInitialized && !manager.isInitializing) {
      await initialize();
    }

    await manager.waitForReady();

    // Use provided scene ID or generate one for multi-instance scenarios
    const id =
      sceneId ||
      (manager.generateSceneId ? manager.generateSceneId() : undefined);
    return manager.createScene(id);
  }, [manager, sceneId, initialize]);

  // Clear scene function
  const clearScene = useCallback(() => {
    if (!manager) {
      return;
    }

    const sceneManager = manager.getSceneManager();
    if (sceneManager) {
      sceneManager.clearScene(sceneId);
    }
  }, [manager, sceneId]);

  // Register component function (placeholder for future component system)
  const registerComponent = useCallback(
    (component: unknown) => {
      if (!scene) {
        console.warn("Scene is not ready for component registration");
        return;
      }
      // Future: Implement component registration system
      console.log("Component registered:", component);
    },
    [scene],
  );

  // Unregister component function (placeholder for future component system)
  const unregisterComponent = useCallback(
    (component: unknown) => {
      if (!scene) {
        return;
      }
      // Future: Implement component unregistration system
      console.log("Component unregistered:", component);
    },
    [scene],
  );

  return {
    clearScene,
    createScene,
    isReady,
    registerComponent,
    scene,
    unregisterComponent,
  };
}
